import React, { useMemo } from "react";
import { Idea } from "../model/types";
import { BgImage } from "@/core/components/bgImage/BgImage";
import { Share } from "@/core/components/share/Share";
import { imageList } from "@/core/utils/imageList";
import { Link } from "react-router-dom";

interface IdeaHeaderProps {
  idea: Idea;
}

export const IdeaHeader: React.FC<IdeaHeaderProps> = ({ idea }) => {
  const lookingForTags = useMemo(() => {
    if (!idea?.looking_for || idea.looking_for.length === 0) return [];
    return idea.looking_for.map((item) =>
      typeof item === "string" ? item : item.name,
    );
  }, [idea?.looking_for]);

  const sectorTags = useMemo(() => {
    if (!idea?.call?.sectors || idea?.call?.sectors?.length === 0) return [];
    return idea?.call?.sectors;
  }, [idea?.call?.sectors]);

  return (
    <>
      <div className="owner-section">
        <div className="owner-info">
          {typeof idea.ownership === "object" &&
          idea.ownership?.logo_uri &&
          idea.ownership.logo_uri !==
            "partner-images/logo_not_available.jpg" ? (
            <img
              src={idea.ownership.logo_uri}
              alt={idea.ownership.title}
              className="owner-logo"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = "none";
                const placeholder = target.nextElementSibling as HTMLElement;
                if (placeholder) placeholder.style.display = "flex";
              }}
            />
          ) : (
            <div className="owner-logo placeholder">
              <BgImage img={imageList.noOrgimage} />
            </div>
          )}

          {typeof idea.ownership === "object" && idea.ownership && (
            <Link to={`/partner/${idea.ownership.id}`} className="owner-link">
              <div className="owner-title">{idea.ownership?.title}</div>
            </Link>
          )}

          <div className="idea-badge">Owner</div>

          {idea?.is_coordinator && !idea?.is_owner && (
            <div className="idea-badge">Coordinator</div>
          )}

          <div className="owner-details">
            <div className="owner-name">
              {typeof idea.ownership === "object"
                ? idea.ownership?.job_title || ""
                : ""}
            </div>
          </div>
        </div>
        <div className="status-tags">
          <Share />
        </div>
      </div>

      <h1 className="idea-title">{idea?.title}</h1>

      <div className="looking-for">
        <span className="looking-label">Looking for</span>
        {lookingForTags.map((tag, index) => (
          <span key={index} className="looking-tag">
            {tag}
            {index < lookingForTags.length - 1 ? ", " : ""}
          </span>
        ))}
      </div>

      <div className="sector-tags">
        {sectorTags.map((sector, index) => (
          <span key={index} className="sector-tag">
            {typeof sector === "string" ? sector : sector.name}
          </span>
        ))}
      </div>

      <div className="description-section">
        <h2>Description</h2>
        <div className="description-text">
          {idea?.description ? (
            idea.description
              .split("\n")
              .map((paragraph, index) => <p key={index}>{paragraph}</p>)
          ) : (
            <p className="no-description">No description available</p>
          )}
        </div>
      </div>
    </>
  );
};
